import type { ErrorEventNotificationSummary } from '../services/api';

/**
 * Pure Entscheidungslogik der Benachrichtigungs-Glocke — bewusst ohne
 * Browser-/API-Abhängigkeiten, damit sie im Node-Vitest testbar ist. Der
 * React-Teil lebt in `useErrorEventNotifications.ts`.
 */

/** Poll-Intervall der Benachrichtigungs-Glocke — bewusst fix, wird auch im UI kommuniziert („alle 30 s"). */
export const NOTIFICATION_POLL_INTERVAL_MS = 30_000;

/** Ergebnis des Vergleichs zweier aufeinanderfolgender Summary-Polls. */
export interface NewActivityDecision {
  /** `true`, wenn seit dem letzten Poll ein neueres offenes Event existiert. */
  notifyEvents: boolean;
  /** `true`, wenn die DLQ-Tiefe seit dem letzten Poll gewachsen ist. */
  notifyDlq: boolean;
}

/** Inhalt einer zu feuernden Desktop-Benachrichtigung samt Klick-Ziel im Portal. */
export interface DesktopNotificationContent {
  title: string;
  body: string;
  /** Portal-Route, die ein Klick auf die Benachrichtigung öffnet. */
  target: string;
}

/**
 * Erkennt neue Aktivität zwischen zwei Polls. Der erste Poll (prev `null`)
 * ist immer Baseline — beim Laden der Seite wird nie benachrichtigt, nur das
 * Badge gefüllt.
 *
 * Events: Anker ist `latestOpenEvent.createdAt` (ein Anstieg von `openTotal`
 * allein könnte auch durch Requeue alter Events entstehen). DLQ: Anker ist die
 * Tiefe; ist eine der beiden Seiten `null` (Management-API nicht erreichbar),
 * wird nicht benachrichtigt statt zu raten.
 */
export function detectNewActivity(
  prev: ErrorEventNotificationSummary | null,
  next: ErrorEventNotificationSummary,
): NewActivityDecision {
  if (prev === null) {
    return { notifyEvents: false, notifyDlq: false };
  }

  const prevLatest = prev.latestOpenEvent?.createdAt ?? null;
  const nextLatest = next.latestOpenEvent?.createdAt ?? null;
  const notifyEvents =
    nextLatest !== null &&
    (prevLatest === null || new Date(nextLatest).getTime() > new Date(prevLatest).getTime());

  const notifyDlq = next.dlqTotal !== null && prev.dlqTotal !== null && next.dlqTotal > prev.dlqTotal;

  return { notifyEvents, notifyDlq };
}

/**
 * Baut die Desktop-Benachrichtigung zur erkannten Aktivität — `null`, wenn
 * nichts zu melden ist. Bei gleichzeitig neuen Events UND DLQ-Zuwachs gibt es
 * EINE Benachrichtigung (Ziel: Events-Seite), damit der Desktop nicht flutet.
 */
export function buildDesktopNotification(
  summary: ErrorEventNotificationSummary,
  decision: NewActivityDecision,
): DesktopNotificationContent | null {
  if (decision.notifyEvents) {
    const latest = summary.latestOpenEvent;
    const eventLine = latest ? `${latest.severity}: ${latest.service} · ${latest.errorIdentifier}` : '';
    const dlqLine = decision.notifyDlq && summary.dlqTotal !== null ? ` — DLQ: ${summary.dlqTotal}` : '';
    return {
      title: `Callbert Admin — neue Error-Events (${summary.openTotal} offen)`,
      body: `${eventLine}${dlqLine}`,
      target: '/error-events',
    };
  }

  if (decision.notifyDlq && summary.dlqTotal !== null) {
    return {
      title: 'Callbert Admin — neue Nachricht in der Dead Letter Queue',
      body: `${summary.dlqTotal} Nachricht(en) in service-errors-dlq`,
      target: '/error-events?tab=dlq',
    };
  }

  return null;
}
